import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useMyUploads,
  requestUpload,
  putToS3,
  createRecordingApi,
  type UploadRow,
} from "../api/transcription";
import { formatLocal } from "../components/formatting";
import { useAuth } from "../auth/useAuth";

function UploadPanel() {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [phase, setPhase] = useState<"idle" | "uploading" | "creating">("idle");
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [doneMsg, setDoneMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function pickFile(f: File | null) {
    setFile(f);
    setErrorMsg(null);
    setDoneMsg(null);
    if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  }

  async function doUpload() {
    if (!file) return;
    setErrorMsg(null);
    try {
      setPhase("uploading");
      setProgress(0);
      const { s3_key, put_url } = await requestUpload(
        file.name,
        file.type || "application/octet-stream"
      );
      await putToS3(put_url, file, setProgress);
      setPhase("creating");
      await createRecordingApi({
        title: title.trim() || file.name,
        s3_key_audio: s3_key,
        original_filename: file.name,
        format: file.name.includes(".") ? file.name.split(".").pop() : undefined,
        size_bytes: file.size,
      });
      await queryClient.invalidateQueries({ queryKey: ["transcription-my-uploads"] });
      await queryClient.invalidateQueries({ queryKey: ["transcription-recordings"] });
      setDoneMsg(`"${title.trim() || file.name}" uploaded.`);
      setFile(null);
      setTitle("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setErrorMsg(String(err));
    } finally {
      setPhase("idle");
    }
  }

  return (
    <div className="tr-upload tr-upload-wide">
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*,.m4a,.mp3,.wav,.aac,.ogg"
        onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
        disabled={phase !== "idle"}
      />
      {file && (
        <>
          <input
            type="text"
            className="tr-upload-title"
            placeholder="Title (e.g. Dr. Plunkett follow-up, Aug 3)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={phase !== "idle"}
          />
          <button className="btn-small" onClick={() => void doUpload()} disabled={phase !== "idle"}>
            {phase === "uploading"
              ? `Uploading… ${progress}%`
              : phase === "creating"
                ? "Registering…"
                : "⬆ Upload"}
          </button>
        </>
      )}
      {phase === "uploading" && (
        <div className="tr-progress">
          <div className="tr-progress-bar" style={{ width: `${progress}%` }} />
        </div>
      )}
      {doneMsg && <div className="tr-upload-done">{doneMsg}</div>}
      {errorMsg && <div className="tr-error">{errorMsg}</div>}
    </div>
  );
}

function familyStatus(u: UploadRow): string {
  return u.processed ? "Completed" : "Received — waiting on Justin";
}

export function UploadView() {
  const auth = useAuth();
  const familyMode = auth.status === "authenticated" && auth.role === "family";
  const { data, isLoading } = useMyUploads();
  const items = data?.items ?? [];

  return (
    <div className="up-layout">
      <h2>Upload a recording</h2>
      <p className="up-hint">
        Audio files only (voice memos are fine). After upload it will be transcribed and
        prepared{familyMode ? " — you'll see it under Transcripts once it's ready." : "."}
      </p>
      <UploadPanel />

      <h2>{familyMode ? "Your uploads" : "All uploads"}</h2>
      {isLoading && <div className="tr-detail-empty">Loading…</div>}
      {!isLoading && items.length === 0 && (
        <div className="tr-detail-empty">Nothing uploaded yet.</div>
      )}
      {items.length > 0 && (
        <table className="up-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Uploaded</th>
              {!familyMode && <th>By</th>}
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id}>
                <td>{u.title}</td>
                <td>{formatLocal(u.created_at)}</td>
                {!familyMode && <td>{u.uploaded_by ?? "—"}</td>}
                <td>
                  {familyMode ? (
                    familyStatus(u)
                  ) : (
                    <span className="up-admin-status">
                      <span className={`tr-status tr-status-${u.status}`}>{u.status}</span>
                      <span>{u.processed ? "✓ processed" : "· unprocessed"}</span>
                      <span>{u.shared ? "👥 shared" : "🔒 private"}</span>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
